import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';
import { Button } from '../common/Button';

interface Props {
  onSubmit: (prompt: string) => void;
  isLoading: boolean;
  error: string | null;
}

const Section = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing['2xl']};
  box-shadow: ${theme.shadows.sm};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.lg};
  }
`;

const Title = styled.h2`
  font-size: ${theme.fontSizes['2xl']};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const Description = styled.p`
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.xl};
  line-height: 1.6;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: ${theme.spacing.lg};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  font-family: ${theme.fonts.body};
  resize: vertical;
  transition: all 0.2s ease;
  background: ${theme.colors.background};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight}20;
  }
  
  &::placeholder {
    color: ${theme.colors.text.muted};
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.muted};
  margin-top: ${theme.spacing.xs};
`;

const ExamplePrompts = styled.div`
  margin-top: ${theme.spacing.xl};
  padding-top: ${theme.spacing.xl};
  border-top: 1px solid ${theme.colors.border};
`;

const ExampleTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
`;

const ExampleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ExampleCard = styled(motion.button)`
  text-align: left;
  padding: ${theme.spacing.md};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.surface};
    border-color: ${theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
  }
`;

const ExampleLabel = styled.div`
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${theme.spacing.xs};
  font-weight: 600;
`;

const ExampleText = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.primary};
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing.xl};
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  border: 1px solid #fca5a5;
  color: #dc2626;
  padding: ${theme.spacing.md};
  border-radius: ${theme.radii.md};
  margin-top: ${theme.spacing.md};
`;

const SubmitButton = styled(Button)`
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  white-space: nowrap;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    padding: ${theme.spacing.md} ${theme.spacing.lg};
  }
`;

const examples = [
  {
    domain: 'Programming',
    prompt: 'Write a Python function to sort a list of dictionaries by a specific key'
  },
  {
    domain: 'Screenwriting',
    prompt: 'Create a dramatic scene between two characters revealing a secret'
  },
  {
    domain: 'Marketing',
    prompt: 'Write email copy for a product launch campaign'
  },
  {
    domain: 'Education',
    prompt: 'Explain photosynthesis to middle school students'
  }
];

export const PromptInputSection: React.FC<Props> = ({ onSubmit, isLoading, error }) => {
  const [prompt, setPrompt] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const handleSubmit = () => {
    if (prompt.trim() && !isLoading) {
      onSubmit(prompt.trim());
    }
  };
  
  const handleExampleClick = (examplePrompt: string) => {
    setPrompt(examplePrompt);
  };
  
  return (
    <Section>
      <Title>Enter Your Prompt</Title>
      <Description>
        Start with your initial prompt idea. Fossil Fills will transform it into a 
        systematic Mad Libs template that teaches prompt engineering principles.
      </Description>
      
      <TextArea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Enter your prompt here... (e.g., 'Write a blog post about AI trends')"
        disabled={isLoading}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.ctrlKey) {
            handleSubmit();
          }
        }}
      />
      <CharCount>{prompt.length} characters</CharCount>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <ButtonContainer>
        <div style={{ fontSize: theme.fontSizes.sm, color: theme.colors.text.muted }}>
          Press Ctrl+Enter to submit
        </div>
        <SubmitButton
          onClick={handleSubmit}
          disabled={!prompt.trim() || isLoading}
        >
          {isLoading ? 'Creating...' : isMobile ? 'Fossilize →' : 'Create Fossil Template →'}
        </SubmitButton>
      </ButtonContainer>
      
      <ExamplePrompts>
        <ExampleTitle>Try an Example</ExampleTitle>
        <ExampleGrid>
          {examples.map((example, index) => (
            <ExampleCard
              key={index}
              onClick={() => handleExampleClick(example.prompt)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ExampleLabel>{example.domain}</ExampleLabel>
              <ExampleText>{example.prompt}</ExampleText>
            </ExampleCard>
          ))}
        </ExampleGrid>
      </ExamplePrompts>
    </Section>
  );
};